// Localização: frontend/src/services/escalaService.js
// Service para comunicação com API de escalas de plantão

import api from '../api/api';

/**
 * Gera sugestão de escala usando IA
 * @param {string} dataInicio - Data de início (YYYY-MM-DD)
 * @param {string} dataFim - Data de fim (YYYY-MM-DD)
 * @param {Object} opcoes - Opções adicionais (regras customizadas, etc.)
 * @returns {Promise<Object>} Sugestão gerada
 */
export const sugerirEscala = async (dataInicio, dataFim, opcoes = {}) => {
    try {
        const response = await api.post('/escalas/sugerir', {
            data_inicio: dataInicio,
            data_fim: dataFim,
            opcoes: opcoes
        });
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao gerar sugestão:', error);
        throw error;
    }
};

/**
 * Lista escalas da organização
 * @param {Object} filtros - Filtros opcionais (status, periodo_inicio, periodo_fim)
 * @returns {Promise<Array>} Array de escalas
 */
export const listarEscalas = async (filtros = {}) => {
    try {
        const params = new URLSearchParams();
        if (filtros.status) params.append('status', filtros.status);
        if (filtros.periodo_inicio) params.append('periodo_inicio', filtros.periodo_inicio);
        if (filtros.periodo_fim) params.append('periodo_fim', filtros.periodo_fim);

        const response = await api.get(`/escalas?${params.toString()}`);
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao listar escalas:', error);
        throw error;
    }
};

/**
 * Obtém uma escala específica
 * @param {number} id - ID da escala
 * @returns {Promise<Object>} Dados da escala
 */
export const obterEscala = async (id) => {
    try {
        const response = await api.get(`/escalas/${id}`);
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao obter escala:', error);
        throw error;
    }
};

/**
 * Aprova uma sugestão de escala
 * @param {number} id - ID da escala
 * @param {Object} ajustes - Ajustes manuais feitos (opcional)
 * @returns {Promise<Object>} Escala aprovada
 */
export const aprovarSugestao = async (id, ajustes = null) => {
    try {
        const response = await api.post(`/escalas/${id}/aprovar`, {
            ajustes: ajustes
        });
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao aprovar sugestão:', error);
        throw error;
    }
};

/**
 * Rejeita uma sugestão de escala
 * @param {number} id - ID da escala
 * @param {string} feedback - Feedback sobre a rejeição
 * @returns {Promise<void>}
 */
export const rejeitarSugestao = async (id, feedback) => {
    try {
        const response = await api.post(`/escalas/${id}/rejeitar`, {
            feedback: feedback
        });
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao rejeitar sugestão:', error);
        throw error;
    }
};

/**
 * Lista todas as preferências da organização
 * @returns {Promise<Array>} Array de preferências
 */
export const listarPreferencias = async () => {
    try {
        const response = await api.get('/escalas/preferencias');
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao listar preferências:', error);
        throw error;
    }
};

/**
 * Obtém preferências de um profissional
 * @param {number} usuarioId - ID do usuário
 * @param {number} pacienteId - ID do paciente (opcional)
 * @returns {Promise<Object|Array>} Preferências do profissional
 */
export const obterPreferenciasProfissional = async (usuarioId, pacienteId = null) => {
    try {
        const url = pacienteId 
            ? `/escalas/preferencias/${usuarioId}?paciente_id=${pacienteId}`
            : `/escalas/preferencias/${usuarioId}`;
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao obter preferências:', error);
        throw error;
    }
};

/**
 * Salva preferências de um profissional
 * @param {Object} preferencias - Dados das preferências
 * @returns {Promise<Object>} Preferências salvas
 */
export const salvarPreferenciasProfissional = async (preferencias) => {
    try {
        const response = await api.post('/escalas/preferencias', preferencias);
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao salvar preferências:', error);
        throw error;
    }
};

/**
 * Exclui preferências de um profissional
 * @param {number} id - ID da preferência
 * @returns {Promise<void>}
 */
export const excluirPreferencias = async (id) => {
    try {
        const response = await api.delete(`/escalas/preferencias/${id}`);
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao excluir preferências:', error);
        throw error;
    }
};

/**
 * Exclui uma escala de plantão
 * @param {number} id - ID da escala
 * @returns {Promise<void>}
 */
export const excluirEscala = async (id) => {
    try {
        const response = await api.delete(`/escalas/${id}`);
        return response.data;
    } catch (error) {
        console.error('[EscalaService] Erro ao excluir escala:', error);
        throw error;
    }
};

